import React, { useRef, useCallback } from 'react';
import { Play, Volume2 } from 'lucide-react';

const Hero = ({ t }) => {
  const videoRef = useRef(null);

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      const offset = 64;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  const handleVideoClick = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    // Try native fullscreen, unmute, and show controls
    video.muted = false;
    video.controls = true;

    const requestFS =
      video.requestFullscreen ||
      video.webkitRequestFullscreen ||
      video.msRequestFullscreen;

    if (requestFS) {
      requestFS.call(video).catch(() => {
        // Fullscreen denied — still unmuted with controls
      });
    }

    const onExitFS = () => {
      // When user exits fullscreen, re-mute and hide controls
      if (
        !document.fullscreenElement &&
        !document.webkitFullscreenElement
      ) {
        video.muted = true;
        video.controls = false;
        document.removeEventListener('fullscreenchange', onExitFS);
        document.removeEventListener('webkitfullscreenchange', onExitFS);
      }
    };

    document.addEventListener('fullscreenchange', onExitFS);
    document.addEventListener('webkitfullscreenchange', onExitFS);
  }, []);

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative pt-16 lg:pt-20"
    >
      {/* Full-width Video Hero */}
      <div
        className="relative w-full h-[70vh] lg:h-[85vh] overflow-hidden cursor-pointer group"
        onClick={handleVideoClick}
      >
        {/* Single Video — plays muted in BG, goes fullscreen+audio on click */}
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster="/images/renders/HA01.jpeg"
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src="/videos/hero.mp4" type="video/mp4" />
        </video>

        {/* Bottom Gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

        {/* Play Button (hover) */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="w-20 h-20 lg:w-24 lg:h-24 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border-2 border-white/50 hover:bg-white/30 transition-all">
            <Play className="w-8 h-8 lg:w-10 lg:h-10 text-white ml-1" fill="white" />
          </div>
        </div>

        {/* Click hint (hover) */}
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="flex items-center gap-2 bg-black/50 backdrop-blur-sm px-4 py-2 rounded-full mt-32">
            <Volume2 className="w-4 h-4 text-white" />
            <span className="text-white text-sm font-medium">Click for audio</span>
          </div>
        </div>

        {/* Content Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 lg:p-12">
          <div className="max-w-7xl mx-auto">
            <div className="max-w-2xl">
              <div className="flex items-center gap-2 mb-4">
                <img
                  src="/images/logos/recurso_63.svg"
                  alt="Hexaparks"
                  className="h-8 sm:h-10 lg:h-12 w-auto brightness-0 invert"
                />
                <div className="flex flex-col items-center leading-none">
                  <img
                    src="/images/logos/hexaparks_wordmark.svg"
                    alt="Hexaparks"
                    className="h-4 sm:h-5 lg:h-6 w-auto brightness-0 invert"
                  />
                  <span className="text-[6px] sm:text-[7px] lg:text-[8px] text-white/70 tracking-[0.15em] uppercase mt-[1px]">
                    {t.logo?.slogan}
                  </span>
                </div>
              </div>

              <p className="text-base sm:text-lg lg:text-xl text-white/90 leading-relaxed mb-4 lg:mb-6 font-light max-w-xl">
                {t.hero.tagline}
              </p>

              <button
                data-testid="hero-cta"
                onClick={(e) => {
                  e.stopPropagation();
                  scrollToContact();
                }}
                className="px-5 sm:px-6 py-2.5 sm:py-3 bg-white text-gray-900 text-xs uppercase tracking-[0.15em] hover:bg-gray-100 transition-colors font-medium"
              >
                {t.hero.cta}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
